// Parallel HubSpot Forms API submission for the "Financing That Fits"
// leasing/financing inquiry drawer on the product page (id:
// leasing_contact_form, snippets/additional-payment-options.liquid,
// rendered by sections/main-product.liquid). A different form from
// #ProductContactForm on the same page (see product-contact-form-hubspot.js)
// -- this one is the financing offcanvas, not the general "Contact Us
// About This Product" tab.
//
// Grouped with the Leasing forms (assets/leasing-full-form.js /
// leasing-pre-approval-form.js on the leasing-pages-update branch), not
// with the "Website Lead Forms" integrations (Contact Us, Product
// Contact, Email for Price, Locations, Knife Sharpening, Parts &
// Service) -- per the requester, this drawer is the same
// leasing/financing funnel as those two, just a lighter entry point
// ("Contact Sales" vs. the full wizard). Its own dedicated HubSpot form
// (fields are much lighter than either leasing wizard), but its custom
// properties belong in the "Leasing Application" property group. Does
// NOT send `inquiry_source` -- that's a Website Lead Forms concept;
// neither leasing form uses it either, each just has its own GUID.
//
// NOT reusing Pre-Approval's product_referral_sku/product_referral_title
// -- checked the live Leasing setup checklist (the actual source of
// truth for that branch's HubSpot state, not just its original fix-pass
// doc) and both are marked deprecated there: a live submission got a
// HubSpot rejection ("isn't a Contact property") because neither was
// ever actually created, and Pre-Approval's code no longer sends them
// (superseded by a newer `equipment_products` multi-product field, a
// different shape -- qty + title + url, no SKU -- built for a richer
// "search and link products" UI this drawer doesn't have). Rather than
// wire this drawer to a property that doesn't exist, or force-fit its
// simple single-product hidden fields into that newer multi-product
// format, this uses two new, clearly-named properties instead.
//
// NOT wired to the near-identical drawer on the standalone /pages/leasing
// page (snippets/leasing-drawer-contact.liquid, assets/leasing-drawer-contact.js)
// -- same form id and mostly the same fields, but a distinct page/snippet.
// Left uncovered here; a separate pass if that page also needs HubSpot sync.
//
// Unlike every other form wired so far, this one has CUSTOM JS validation
// (assets/leasing-drawer.js) that calls preventDefault() on invalid
// email/phone input, on top of native `required`/`pattern` attributes.
// leasing-drawer.js registers its 'submit' listener inside its own
// DOMContentLoaded callback, and its <script> tag (no `defer`) runs
// synchronously, earlier in the page, than this one. To land a 'submit'
// listener AFTER its listener -- so event.defaultPrevented below reflects
// whether that handler already blocked the submission -- this script also
// runs synchronously (no `defer` on its tag in
// snippets/additional-payment-options.liquid) and also waits for its own
// DOMContentLoaded callback, preserving the same registration order.
(function () {
  const HUBSPOT_PORTAL_ID = '342999960'; // same portal as every other HubSpot integration
  const HUBSPOT_FORM_GUID = '3b25af50-281e-4088-91fa-32511e1f8234'; // "Product Financing" HubSpot form

  document.addEventListener('DOMContentLoaded', function () {
    const formEl = document.getElementById('leasing_contact_form');
    if (!formEl) return;
    if (formEl.dataset.hubspotWired) return;
    formEl.dataset.hubspotWired = 'true';

    // Same first-space split as every other HubSpot integration in this
    // project -- this form also has one combined "Name" field.
    function splitName(fullName) {
      const trimmed = (fullName || '').trim().replace(/\s+/g, ' ');
      if (!trimmed) return { firstName: '', lastName: '' };
      const spaceIndex = trimmed.indexOf(' ');
      if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
      return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
    }

    function buildHubSpotFields() {
      const fields = [];
      const push = (name, value) => {
        const v = (value ?? '').toString().trim();
        if (v) fields.push({ name, value: v });
      };
      const byId = (id) => (document.getElementById(id)?.value || '').toString().trim();
      // contact[sku] and contact[product_title] have no id in the markup --
      // looked up by name attribute, scoped to this form so the drawer's
      // own instance is used, not any other form on the page.
      const byName = (name) => (formEl.querySelector(`input[name="${name}"]`)?.value || '').toString().trim();
      const { firstName, lastName } = splitName(byId('LeasingContactFormName'));

      push('email', byId('LeasingContactFormEmail'));
      push('firstname', firstName);
      push('lastname', lastName);
      push('phone', byId('LeasingContactFormPhone'));
      push('company', byId('LeasingContactBusinessName'));
      // New properties, "Leasing Application" group -- see the header
      // comment for why these aren't product_referral_sku/title.
      push('financing_referral_sku', byName('contact[sku]'));
      push('financing_referral_title', byName('contact[product_title]'));
      // New -- the selected term (24/36/48/60 months). Optional: the plan
      // radios aren't `required`, so a visitor can reach Contact Sales
      // without picking one. Filed under "Leasing Application", not
      // "Website Lead Forms" -- this concept doesn't exist on either
      // leasing wizard, it's specific to this drawer.
      push('requested_financing_term', byId('selectedFinancingTerm--leasing'));

      return fields;
    }

    // Same fetchClientIp() as every other HubSpot integration in this
    // project.
    function fetchClientIp(timeoutMs = 1500) {
      return new Promise((resolve) => {
        const controller = new AbortController();
        const timer = setTimeout(() => { controller.abort(); resolve(null); }, timeoutMs);
        fetch('https://api.ipify.org?format=json', { signal: controller.signal, keepalive: true })
          .then((r) => r.json())
          .then((data) => { clearTimeout(timer); resolve(data && data.ip ? data.ip : null); })
          .catch(() => { clearTimeout(timer); resolve(null); });
      });
    }

    async function submitToHubSpot() {
      if (!HUBSPOT_PORTAL_ID || !HUBSPOT_FORM_GUID) {
        console.warn('HubSpot sync skipped: HUBSPOT_PORTAL_ID / HUBSPOT_FORM_GUID not configured yet.');
        return;
      }
      try {
        const hutkMatch = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
        const ip = await fetchClientIp();
        const payload = {
          fields: buildHubSpotFields(),
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            ...(hutkMatch ? { hutk: hutkMatch[1] } : {}),
            ...(ip ? { ipAddress: ip } : {}),
          },
        };
        fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch((err) => console.warn('HubSpot submission failed (Shopify submission is unaffected):', err));
      } catch (err) {
        console.warn('HubSpot submission failed (Shopify submission is unaffected):', err);
      }
    }

    formEl.addEventListener('submit', (event) => {
      // leasing-drawer.js's own submit handler runs first (registration
      // order) and calls preventDefault() when its email/phone validation
      // fails -- skip firing HubSpot for a submission that never actually
      // reaches Shopify.
      if (event.defaultPrevented) return;
      submitToHubSpot();
    });
  });
})();
